import {observer} from 'mobx-react';
import {
  ComponentType,
  lazy,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router';
import {ObjectivesPage} from '../pages/dashboard/goals';
import {CheckinsPage} from '../pages/dashboard/checkins';
import {NotificationPage} from '../pages/dashboard/notifications/notification-page';
import {MemberProfilePageElement} from '../pages/dashboard/member-profile/member-profile-page';
import {AdminWorkspacePage} from '../pages/dashboard/admin/admin-components/admin-workspace/admin-workspace-page';
import {canAdministerCheckins} from '../pages/dashboard/checkins/checkins-access';
import {useStoreContext} from '../store/store-context';
import {NavigationOption} from '@ui/interface';
import {CompanyReporting} from '@pages/dashboard/reporting/company-reporting';
import {Modal} from '../ui/molecules/modal/modal';
import {ViewUserCheckins} from '@pages/dashboard/checkins/direct_reports/view-user-checkins/view-user-checkins';
import {WorkspaceInviteDialog} from '../pages/dashboard/admin/admin-components/invite/workspace-invite-dialog';
import {IntegrationsController} from '../pages/dashboard/admin/admin-components/admin-integrations/components/app.controller';
import {illustrationList} from '@ui/assets/illustrations';
import {GoalsRequestImpl} from '../api/goals-api/goals-request';
import {GoalsController} from '../api/goals-api/goals-controller';
import {EndGoal} from '@pages/dashboard/goals/end-goal';
import FreeTierModal from '@ui/organisms/free-tier/free-tier-modal';
import {UserAPIRequestImpl} from '../api/user-api/user-api-request';
import {UserAPIController} from '../api/user-api/user-api-controller';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {WorkspaceCheckinsSettingsRequestImpl} from '../pages/dashboard/admin/admin-components/admin-workspace/workspace-engagements/admin-checkins/admin-checkins.request';
import {WorkspaceCheckinsSettingsController} from '../pages/dashboard/admin/admin-components/admin-workspace/workspace-engagements/admin-checkins/admin-checkins.controller';
import {RedirectTo} from './create-routes';
import {AdminBillingRequestImpl} from '@pages/dashboard/admin/admin-components/admin-workspace/admin-billing-setup/admin-billing.request';
import {AdminBillingController} from '@pages/dashboard/admin/admin-components/admin-workspace/admin-billing-setup/admin-billing.controller';
import {DeleteGoal} from '@pages/dashboard/goals/delete-goal';
import {ArchiveGoal} from '@pages/dashboard/goals/archive-goal';
import {ReactivateGoal} from '@pages/dashboard/goals/reactivate-goal';
import ReportingDashboard from '@pages/dashboard/reporting/dashboard';
import {useKpiInsightHook} from '@hooks/kpi-insights';
import {useOneTimeFetch} from '@hooks/query-hook';
import {useQueryClient} from 'react-query';
import {invalidateGoalQueries} from '@utils/goal-query-cache';
import {
  PaymentRequiredModal,
  SubscriptionExpiredModal,
  TrialEndedModal,
} from '@ui/organisms/modal/subscription-expired-modal';
import {
  deriveBillingState,
  getAccessRecoveryNotice,
  isSubscriptionExpired,
} from '@utils/billing-state';
import {Feedback} from '@pages/feedback';
import {
  isBillingRecoveryRoute,
  isPaymentRecoveryRoute,
} from './billing-recovery-route';

const DashboardTemplate = lazy(() =>
  import('../ui/templates/dashboard').then((module) => ({
    default: module.DashboardTemplate,
  })),
);

declare const window: any;

export const Dashboards = observer(
  ({
    header,
    navigations,
    WorkspaceDropdown,
    workspace,
  }: {
    header: ReactNode;
    navigations: NavigationOption[];
    WorkspaceDropdown?: ComponentType<{onClose: () => void}>;
    workspace?: {
      avatar?: string;
      name: string;
    };
  }) => {
    const {
      usersStore: {getUsers, fetchDeactivatedUsers, setCurrentUserGroups},
      billingsStore: {showFreeTierModals, updateShowFreeTierModals},
      authStore: {
        globalUserInvitationPopupState,
        handleOpen,
        closeGlobalUserInvitationModal,
      },
      goalsState: {setApps},
      integrationStore: {setActiveIntegrations},
      checkinsStore: {setCheckinsConfiguration},
    } = useStoreContext();

    const navigate = useNavigate();
    const {pathname} = useLocation();

    const {
      isCheckinsEnabled,
      isGoalEnabled,
      isFeedBackEnabled,
      isEngagementActive,
      isPerformanceActive,
    } = useWorkspaceUseCase();

    useKpiInsightHook();

    const fetchCurrentUserGroups = useCallback(async () => {
      const request = new UserAPIRequestImpl();
      const controller = new UserAPIController(request);
      const response: any = await controller.fetchCurrentUserGroups();

      response && setCurrentUserGroups(response);
    }, [setCurrentUserGroups]);

    useEffect(() => {
      getUsers();
      fetchCurrentUserGroups();
    }, [fetchCurrentUserGroups, fetchDeactivatedUsers, getUsers, handleOpen]);

    const {
      storeYouGoals: {endGoalInStore},
      storeAllObjectives: {
        setGoals,
        goals_,
        endGoalInAllObjectiveStore,
        modifyYouGoalsPaginator,
        youGoalsPaginator,
      },
      companyStore: {setDirectReports, directReports},
      checkinsStore: {
        endGoalInCheckins,
        archiveGoalInCheckins,
        deleteGoalInCheckins,
      },
      goalsState,
      authStore: {auth},
      billingsStore: {updateShowNonActiveBilling, setBillingSubscriptions},
    } = useStoreContext();

    const billingState = deriveBillingState(auth?.user?.workspace?.billing);
    const trailHasEnded = billingState === 'trial_expired';
    const canManageBilling = auth?.user?.role === 'admin';

    const isFreePlanActive =
      auth?.user?.workspace?.billing?.plan === 'free_tier';

    const subscriptionHasEnded = isSubscriptionExpired(
      auth?.user?.workspace?.billing,
    );
    const accessRecoveryNotice = getAccessRecoveryNotice(
      auth?.user?.workspace?.billing,
    );
    const accessRestricted = accessRecoveryNotice !== null;
    const isBillingRecoveryPage = isBillingRecoveryRoute(pathname);
    const isPaymentRecoveryPage =
      canManageBilling && isPaymentRecoveryRoute(pathname);
    const canManageCheckins = canAdministerCheckins(auth.user.role);

    useEffect(() => {
      if (accessRestricted && globalUserInvitationPopupState) {
        closeGlobalUserInvitationModal();
      }
    }, [
      accessRestricted,
      closeGlobalUserInvitationModal,
      globalUserInvitationPopupState,
    ]);

    const getIntegrationName = () => {
      const pathname = window.location.pathname;

      if (pathname.includes('admin/apps')) {
        const segments = pathname.split('/');
        const appName = segments[segments.indexOf('apps') + 1];

        return appName;
      }
      return '';
    };

    const queryClient = useQueryClient();

    const getActiveIntegrations = useCallback(async () => {
      const controller = new IntegrationsController();
      const response = await controller.getIntegrations();

      response && setApps(response);
      response && setActiveIntegrations(response);
    }, [setApps, setActiveIntegrations]);

    useOneTimeFetch('activeIntegrations', () => getActiveIntegrations(), {
      enabled: true,
    });

    const workspaceCheckinsRequest = useMemo(
      () => new WorkspaceCheckinsSettingsRequestImpl(),
      [],
    );

    const workspaceCheckinsController = useMemo(
      () => new WorkspaceCheckinsSettingsController(workspaceCheckinsRequest),
      [workspaceCheckinsRequest],
    );

    const getCheckinsConfigData = useCallback(async () => {
      const response = await workspaceCheckinsController.getCheckinsSettings();
      response && setCheckinsConfiguration(response);
    }, [setCheckinsConfiguration, workspaceCheckinsController]);

    useOneTimeFetch('checkinsconfigdata', () => getCheckinsConfigData(), {
      enabled: true,
    });

    const [reOpenGoalLoader, setReOpenGoalLoader] = useState(false);

    const getBillingMethods = async () => {
      const request = new AdminBillingRequestImpl();
      const controller = new AdminBillingController(request);

      const response = await controller.fetchAllBillingSubscriptions();
      response && setBillingSubscriptions(response);
    };

    useOneTimeFetch(['billing-methods'], () => getBillingMethods(), {
      enabled: auth.user.role === 'admin',
    });

    const reActivateGoal = async () => {
      setReOpenGoalLoader(true);

      try {
        const request = new GoalsRequestImpl();
        const controller = new GoalsController(request);
        const response = await controller.reactivateGoal(
          goalsState.reactivate_goal.id,
          goalsState.goalFramework,
        );
        response && setReOpenGoalLoader(false);
        response &&
          goalsState.setReactivateGoal({
            reOpen: false,
            id: '',
            name: '',
          });
      } catch (error) {
        setReOpenGoalLoader(false);
      }
    };

    const showExpiredFeatureRoutes = subscriptionHasEnded && !trailHasEnded;
    const showPerformanceRoutes =
      (isPerformanceActive && !isFreePlanActive) || showExpiredFeatureRoutes;
    const showEngagementRoutes =
      (isEngagementActive && !isFreePlanActive) || showExpiredFeatureRoutes;
    const showGoals = showPerformanceRoutes && isGoalEnabled;

    return (
      <>
        <DashboardTemplate
          header={header}
          navigations={navigations}
          workspace={workspace}
          WorkspaceDropdown={WorkspaceDropdown}
          outerStyle={{
            backgroundColor: 'var(--beam-canvas)',
            maxHeight: '100%',
            position: 'relative',
            minHeight: '100vh',
          }}>
          {globalUserInvitationPopupState && !accessRestricted && (
            <Modal
              open={globalUserInvitationPopupState}
              zIndex={30}
              onClose={() => closeGlobalUserInvitationModal()}>
              <WorkspaceInviteDialog
                onClose={() => closeGlobalUserInvitationModal()}
              />
            </Modal>
          )}
          <FreeTierModal
            open={!!showFreeTierModals.date_filter}
            image={
              showFreeTierModals.date_filter === 'custom'
                ? illustrationList.customTimeline
                : illustrationList.quarterlyTimeline
            }
            onClose={() => updateShowFreeTierModals('date_filter', '')}
            description={`This feature allows you to adjust the timeline according to your needs, making it easier to identify trends and patterns that can inform your decision-making process.`}
            title={`Gain Valuable Insights with Historical Quarterly Reports`}
          />
          <FreeTierModal
            open={showFreeTierModals.invite_user}
            image={illustrationList.goldenBarrow}
            onClose={() => updateShowFreeTierModals('invite_user', false)}
            description={`It appears that you have reached the maximum number of members allowed for this workspace. In order to collaborate with a larger team, assign tasks more effectively, and ultimately accomplish your goals more quickly, you may wish to consider adding more members to your workspace.`}
            title={`Opps, you're fresh out of available seats`}
          />
          <SubscriptionExpiredModal
            open={
              accessRecoveryNotice === 'subscription_ended' &&
              !isBillingRecoveryPage
            }
            onUpdateDetails={() => {
              updateShowNonActiveBilling(true);
              navigate('/workspace-settings/billing');
            }}
          />
          <PaymentRequiredModal
            open={
              accessRecoveryNotice === 'payment_required' &&
              !isPaymentRecoveryPage
            }
            canManageBilling={canManageBilling}
            onReviewBilling={() =>
              navigate('/workspace-settings/billing', {replace: true})
            }
          />
          <TrialEndedModal
            open={
              accessRecoveryNotice === 'trial_ended' && !isBillingRecoveryPage
            }
            workspaceName={auth?.user?.workspace?.name || ''}
            onBuyPlan={() => {
              updateShowNonActiveBilling(true);
              navigate('/workspace-settings/billing');
            }}
          />
          <Routes>
            {showGoals && (
              <>
                <Route path="/goals" element={<ObjectivesPage />} />
                <Route path="/goals/kpis" element={<ObjectivesPage />} />
                <Route path="/goals/:id" element={<ObjectivesPage />} />
              </>
            )}

            <Route
              path="/home/*"
              element={
                trailHasEnded || subscriptionHasEnded ? (
                  <div></div>
                ) : showGoals ? (
                  <Navigate to="/goals" replace />
                ) : (
                  <div></div>
                )
              }
            />
            <Route path="/alerts/*" element={<NotificationPage />} />

            <Route path="/edit/*" element={<MemberProfilePageElement />} />
            <Route
              path="/workspace-settings/*"
              element={<AdminWorkspacePage />}
            />

            {showEngagementRoutes && (
              <>
                {isCheckinsEnabled && (
                  <>
                    <Route path="/checkins" element={<CheckinsPage />} />
                    <Route
                      path="/checkins/direct-reports"
                      element={
                        <Navigate
                          to={
                            canManageCheckins
                              ? '/checkins/team-management'
                              : '/checkins/my-checkins'
                          }
                          replace
                        />
                      }
                    />
                    <Route
                      path="/checkins/team-management"
                      element={
                        canManageCheckins ? (
                          <CheckinsPage tab="team-management" />
                        ) : (
                          <Navigate to="/checkins/my-checkins" replace />
                        )
                      }
                    />
                    <Route
                      path="/checkins/team-management/submissions"
                      element={
                        <Navigate
                          to={
                            canManageCheckins
                              ? '/checkins/team-management'
                              : '/checkins/my-checkins'
                          }
                          replace
                        />
                      }
                    />
                    <Route
                      path="/checkins/team-management/cycles"
                      element={
                        <Navigate
                          to={
                            canManageCheckins
                              ? '/checkins/team-management'
                              : '/checkins/my-checkins'
                          }
                          replace
                        />
                      }
                    />
                    <Route
                      path="/checkins/questions"
                      element={<CheckinsPage tab="questions" />}
                    />
                    <Route
                      path="/checkins/my-checkins"
                      element={<CheckinsPage tab="my-checkins" />}
                    />
                    <Route
                      path="/checkins/history"
                      element={<CheckinsPage tab="history" />}
                    />
                    <Route
                      path="/view-user-checkins/:checkinsId/:type"
                      element={<ViewUserCheckins />}
                    />
                  </>
                )}
                {isFeedBackEnabled && (
                  <Route path="/feedback/*" element={<Feedback />} />
                )}
              </>
            )}

            <Route
              path="/apps/*"
              element={<RedirectTo redirectTo="/edit/apps" />}
            />

            <Route
              path="/admin/apps/people"
              element={<RedirectTo redirectTo="/edit/apps" />}
            />

            <Route
              path="/admin/apps/*"
              element={
                <RedirectTo redirectTo={`/edit/apps/${getIntegrationName()}`} />
              }
            />

            <Route
              path="/manage-people"
              element={<RedirectTo redirectTo="/workspace-settings/members" />}
            />
            <Route path="/reporting/*" element={<CompanyReporting />} />
            <Route
              path="/reporting/dashboard"
              element={<ReportingDashboard />}
            />

            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>

          {goalsState.delete && (
            <DeleteGoal
              handleContinue={async () => {
                deleteGoalInCheckins(goalsState?.deleteId);

                const request = new GoalsRequestImpl();
                const controller = new GoalsController(request);

                const deleted = await controller.deleteGoal(
                  goalsState.deleteId,
                );

                if (!deleted) return;

                await invalidateGoalQueries(queryClient);

                goals_ &&
                  setGoals(
                    goals_?.filter(
                      (goal: any) => goal.id !== goalsState.deleteId,
                    ),
                  );

                youGoalsPaginator.totalResults &&
                  modifyYouGoalsPaginator(
                    'totalResults',
                    youGoalsPaginator.totalResults - 1,
                  );

                directReports &&
                  setDirectReports(
                    directReports?.map((report: any) => {
                      return {
                        ...report,
                        goals: report.goals?.filter(
                          (goal: any) => goal.id !== goalsState.deleteId,
                        ),
                      };
                    }),
                  );
              }}
            />
          )}
          {goalsState.end && (
            <EndGoal
              endGoal={endGoalInStore}
              endGoalInAllObjectiveStore={endGoalInAllObjectiveStore}
              endCheckinGoal={endGoalInCheckins}
            />
          )}
          {goalsState.archive && (
            <ArchiveGoal
              handleContinue={() => {
                archiveGoalInCheckins(goalsState.archiveId);
              }}
            />
          )}
          {goalsState.reactivate_goal.reOpen && (
            <ReactivateGoal
              reActivateGoal={reActivateGoal}
              reOpenGoalLoader={reOpenGoalLoader}
            />
          )}
        </DashboardTemplate>
      </>
    );
  },
);
